import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  MapRegionTranslationPatch,
  MapRegionTranslationRecord,
  MapTopLevelTranslationPatch,
  MapTopLevelTranslationRecord,
  MapTranslationProjection,
  MapVersion,
  MapType,
} from '@course-authoring/shared-types';
import { fetchWithAuthRetry, parseApiError } from './apiUtils';
import { invalidateLessonLocaleCoverage } from './lessonLocaleCoverageQueries';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const mapKeys = {
  all: ['maps'] as const,
  lists: () => [...mapKeys.all, 'list'] as const,
  details: () => [...mapKeys.all, 'detail'] as const,
  detail: (id: string) => [...mapKeys.details(), id] as const,
  translations: () => [...mapKeys.all, 'translations'] as const,
  translationMap: (id: string) => [...mapKeys.translations(), id] as const,
  translation: (id: string, locale: string) =>
    [...mapKeys.translationMap(id), locale] as const,
};

export interface ApiError {
  status: number;
  code: string;
  message: string;
  retryable: boolean;
}

export interface CreateMapVersionInput {
  mapType: MapType;
  title: string;
  description?: string;
  interactionMessage?: string;
  regions?: Array<{
    regionKey: string;
    name: string;
    description?: string;
  }>;
}

export interface UpdateMapVersionInput {
  mapType?: MapType;
  title?: string;
  description?: string;
  interactionMessage?: string;
  regions?: Array<{
    regionKey: string;
    name: string;
    description?: string;
  }>;
}

function toApiError(response: Response, fallbackMessage: string): ApiError {
  return {
    status: response.status,
    code: `HTTP_${response.status}`,
    message: response.statusText || fallbackMessage,
    retryable: response.status >= 500 || response.status === 429,
  };
}

async function fetchMapVersions(): Promise<MapVersion[]> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/maps`);
  if (!response.ok) throw toApiError(response, 'Failed to fetch map versions');
  return response.json();
}

async function fetchMapVersion(id: string): Promise<MapVersion> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/maps/${id}`);
  if (!response.ok) throw toApiError(response, 'Failed to fetch map version');
  return response.json();
}

async function createMapVersion(input: CreateMapVersionInput): Promise<MapVersion> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/maps`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!response.ok) throw toApiError(response, 'Failed to create map version');
  return response.json();
}

async function updateMapVersion(id: string, input: UpdateMapVersionInput): Promise<MapVersion> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/maps/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  if (!response.ok) throw toApiError(response, 'Failed to update map version');
  return response.json();
}

async function duplicateMapVersion(id: string): Promise<MapVersion> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/maps/${id}/duplicate`, {
    method: 'POST',
  });
  if (!response.ok) throw toApiError(response, 'Failed to duplicate map version');
  return response.json();
}

async function deleteMapVersion(id: string): Promise<void> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/maps/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw toApiError(response, 'Failed to delete map version');
}

export async function fetchMapTranslations(
  id: string,
  locale: string,
): Promise<MapTranslationProjection> {
  const query = new URLSearchParams({ locale });
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/maps/${id}/translations?${query}`,
  );
  if (!response.ok) throw await parseApiError(response, 'Map translation request failed');
  return response.json();
}

export async function patchMapTopLevelTranslation(
  id: string,
  locale: string,
  patch: MapTopLevelTranslationPatch,
): Promise<MapTopLevelTranslationRecord> {
  const query = new URLSearchParams({ locale });
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/maps/${id}/translations/top-level?${query}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  if (!response.ok) throw await parseApiError(response, 'Map translation request failed');
  return response.json();
}

export async function patchMapRegionTranslation(
  id: string,
  regionId: string,
  locale: string,
  patch: MapRegionTranslationPatch,
): Promise<MapRegionTranslationRecord> {
  const query = new URLSearchParams({ locale });
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/maps/${id}/translations/regions/${regionId}?${query}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  if (!response.ok) throw await parseApiError(response, 'Map translation request failed');
  return response.json();
}

export function useMapVersions() {
  return useQuery({
    queryKey: mapKeys.lists(),
    queryFn: fetchMapVersions,
  });
}

export function useMapVersion(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: mapKeys.detail(id),
    queryFn: () => fetchMapVersion(id),
    enabled: options?.enabled ?? !!id,
  });
}

export function useMapTranslations(
  id: string,
  locale: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: mapKeys.translation(id, locale),
    queryFn: () => fetchMapTranslations(id, locale),
    enabled: options?.enabled ?? Boolean(id),
    // A projection is an editable locale-specific record. Reusing the prior
    // query key's data can hydrate and later persist values into the new locale.
    placeholderData: undefined,
    staleTime: 0,
  });
}

export function useMapTopLevelTranslationMutation(
  id: string,
  locale: string,
  options?: {
    onSuccess?: (record: MapTopLevelTranslationRecord) => void;
    onError?: (error: ApiError) => void;
  },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: MapTopLevelTranslationPatch) =>
      patchMapTopLevelTranslation(id, locale, patch),
    onSuccess: (record) => {
      queryClient.setQueryData<MapTranslationProjection>(
        mapKeys.translation(id, locale),
        (current) => current ? { ...current, topLevel: record } : current,
      );
      if (locale === 'en') {
        queryClient.invalidateQueries({ queryKey: mapKeys.translationMap(id) });
      }
      queryClient.invalidateQueries({ queryKey: mapKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: mapKeys.lists() });
      invalidateLessonLocaleCoverage(queryClient);
      options?.onSuccess?.(record);
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as ApiError);
      }
    },
  });
}

export function useMapRegionTranslationMutation(
  id: string,
  locale: string,
  options?: {
    onSuccess?: (record: MapRegionTranslationRecord) => void;
    onError?: (error: ApiError) => void;
  },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ regionId, patch }: {
      regionId: string;
      patch: MapRegionTranslationPatch;
    }) => patchMapRegionTranslation(id, regionId, locale, patch),
    onSuccess: (record) => {
      queryClient.setQueryData<MapTranslationProjection>(
        mapKeys.translation(id, locale),
        (current) => current ? {
          ...current,
          regions: current.regions.map((region) =>
            region.regionId === record.regionId ? record : region),
        } : current,
      );
      if (locale === 'en') {
        queryClient.invalidateQueries({ queryKey: mapKeys.translationMap(id) });
      }
      queryClient.invalidateQueries({ queryKey: mapKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: mapKeys.lists() });
      invalidateLessonLocaleCoverage(queryClient);
      options?.onSuccess?.(record);
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as ApiError);
      }
    },
  });
}

export function useCreateMapVersion(options?: {
  onSuccess?: (data: MapVersion) => void;
  onError?: (error: ApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateMapVersionInput) => createMapVersion(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: mapKeys.lists() });
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as ApiError);
      }
    },
  });
}

export function useUpdateMapVersion(options?: {
  onSuccess?: (data: MapVersion) => void;
  onError?: (error: ApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateMapVersionInput }) =>
      updateMapVersion(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: mapKeys.lists() });
      queryClient.setQueryData(mapKeys.detail(data.id), data);
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as ApiError);
      }
    },
  });
}

export function useDuplicateMapVersion(options?: {
  onSuccess?: (data: MapVersion) => void;
  onError?: (error: ApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => duplicateMapVersion(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: mapKeys.lists() });
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as ApiError);
      }
    },
  });
}

export function useDeleteMapVersion(options?: {
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteMapVersion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mapKeys.lists() });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as ApiError);
      }
    },
  });
}
